import { getCodeUrl } from '@/common/utils/url';
import { AppDataTransfer_HOSTER } from '@toilet-paper-games/core';
import { makeAutoObservable, autorun, toJS } from 'mobx';
import { HosterAppModel } from './HosterAppModel';
import { HosterPlayerModelHandler } from './HosterPlayerModelHandler';
import { GameInfo } from '@/types';

export class HosterInjectedGameDataHandler {
  /** userId to HosterPlayerModelHandler */
  playerModelHandlersMap: Map<string, HosterPlayerModelHandler> = new Map();
  ready = false;

  reactionDestructors: (() => void)[] = [];

  get playerModelHandlers() {
    return (
      this.playerModelHandlersMap && Array.from(this.playerModelHandlersMap.values())
    );
  }

  constructor(
    private game: GameInfo,
    private appModel: HosterAppModel,
    public lobbyGame: boolean,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setup() {
    this.playerModelHandlersMap = new Map();
    this.ready = false;
    this.reactionDestructors = [];

    this.setupReactions();
    this.setupStorage();

    return () => {
      this.destroyReactions();
    };
  }

  setupStorage() {
    this.fetchGameStorage().then((storage) => {
      this.gameStorage = storage;
    });
  }

  get gameStorageKey() {
    return `tpg-hoster-game-${this.game.uniqueName}-storage`;
  }

  gameStorage: Record<string, string | undefined> | null = null;

  // This is async for possible change to use firebase later
  async fetchGameStorage(): Promise<Record<string, string | undefined>> {
    const content = localStorage.getItem(this.gameStorageKey);

    if (!content) {
      return {};
    }

    return JSON.parse(content);
  }

  async updateGameStorage(key: string, value: string | undefined) {
    const content = await this.fetchGameStorage();

    content[key] = value;

    localStorage.setItem(this.gameStorageKey, JSON.stringify(content));
    this.gameStorage = content;
  }

  setupReactions() {
    this.destroyReactions();

    this.reactionDestructors.push(
      autorun(() => {
        if (!this.playerModelHandlersMap) {
          return;
        }

        for (const user of this.appModel.networkHandler.userManager.users) {
          if (!this.playerModelHandlersMap.has(user.id)) {
            const handler = new HosterPlayerModelHandler(user);
            this.playerModelHandlersMap.set(user.id, handler);
            this.reactionDestructors.push(() => handler.destroy());
          }
        }
      }),
    );

    this.reactionDestructors.push(
      autorun(() => {
        if (!this.playerModelHandlersMap) {
          return;
        }

        for (const [userId, handler] of this.playerModelHandlersMap) {
          if (!this.appModel.networkHandler.userManager.usersMap.has(userId)) {
            handler.destroy();
            this.playerModelHandlersMap.delete(userId);
          }
        }
      }),
    );
  }

  destroyReactions() {
    for (const destructor of this.reactionDestructors) {
      destructor();
    }

    this.reactionDestructors = [];
  }

  get injectedGameData(): AppDataTransfer_HOSTER['data'] {
    const uuid = this.appModel.uuid;

    if (!uuid) {
      throw new Error('No connectionId found');
    }

    if (!this.playerModelHandlers) {
      throw new Error('No playerModelHandlers found');
    }

    return {
      // TODO: Determine if this should be added
      // ready: this.ready,
      connectionId: uuid,
      players: toJS(
        this.playerModelHandlers.map((player) => toJS(player.playerModel.dto)),
      ),
      joinUrl: getCodeUrl(uuid),
      joinCode: uuid,
      devMode: this.appModel.devMode,
      lobbyGame: this.lobbyGame,

      gameStorage: toJS(this.gameStorage),
      globalSettings: toJS(this.appModel.globalSettings),
    };
  }
}
